import type {
  AvailabilityResult,
  CodeLookupResult,
  PaginatedResponse,
  StandardDonation,
  StandardDonationAvailabilityParams,
  StandardDonationCodeLookupParams,
  StandardDonationCodeParams,
  StandardDonationExpandable,
  StandardDonationListOptions,
  StandardDonationStateParams,
  StandardDonationSubmitParams,
} from '@types'
import { apiRequest, paginatedRequest } from './base'

export const standardDonationService = {
  /**
   * @roles charity, charity_driver, captain
   */
  async list<E extends StandardDonationExpandable = never>(
    options: StandardDonationListOptions & { expand?: E | E[] } = {}
  ): Promise<PaginatedResponse<StandardDonation>> {
    return paginatedRequest('/standard_donations', {
      method: 'GET',
      params: options,
    })
  },

  /**
   * @roles charity, charity_driver, captain
   */
  async get<E extends StandardDonationExpandable = never>(
    id: number,
    options: { expand?: E | E[] } = {}
  ): Promise<StandardDonation> {
    return apiRequest(`/standard_donations/${id}`, {
      method: 'GET',
      params: options.expand
        ? {
            expand: Array.isArray(options.expand)
              ? options.expand.join(',')
              : options.expand,
          }
        : {},
    })
  },

  /**
   * @roles charity, captain
   */
  async create(data: Partial<StandardDonation>): Promise<StandardDonation> {
    return apiRequest('/standard_donations', { method: 'POST', body: data })
  },

  /**
   * @roles charity, charity_driver, captain
   */
  async update(
    id: number,
    data: Partial<StandardDonation>
  ): Promise<StandardDonation> {
    return apiRequest(`/standard_donations/${id}`, {
      method: 'PUT',
      body: data,
    })
  },

  /**
   * @roles charity, captain
   */
  async delete(id: number): Promise<void> {
    return apiRequest(`/standard_donations/${id}`, { method: 'DELETE' })
  },

  /**
   * @roles charity, captain
   */
  async restore(id: number): Promise<StandardDonation> {
    return apiRequest(`/standard_donations/${id}/restore`, { method: 'POST' })
  },

  /**
   * @roles public, charity, captain
   */
  async submit(
    data: StandardDonationSubmitParams = {}
  ): Promise<StandardDonation> {
    return apiRequest(`/standard_donations/submit`, {
      method: 'POST',
      body: data,
    })
  },

  /**
   * @roles public, charity, captain
   */
  async codeLookup(
    data: StandardDonationCodeLookupParams = {}
  ): Promise<CodeLookupResult> {
    return apiRequest(`/standard_donations/code`, {
      method: 'POST',
      body: data,
    })
  },

  /**
   * @roles public, charity, captain
   */
  async code(
    code: string | number,
    options: StandardDonationCodeParams = {}
  ): Promise<any> {
    return apiRequest(`/standard_donations/code/${code}`, {
      method: 'GET',
      params: options,
    })
  },

  /**
   * @roles charity_driver, charity, captain
   */
  async state(
    id: string | number,
    data: StandardDonationStateParams = {}
  ): Promise<StandardDonation> {
    return apiRequest(`/standard_donations/${id}/state`, {
      method: 'POST',
      body: data,
    })
  },

  /**
   * @roles public, charity, captain
   */
  async availability(
    options: StandardDonationAvailabilityParams = {}
  ): Promise<AvailabilityResult> {
    return apiRequest(`/standard_donations/availability`, {
      method: 'GET',
      params: options,
    })
  },
}
